import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Box } from '@mui/material';
import { isAxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import ApsDataExtractionDiscoveryModal from '@/components/ApsDataExtractionDiscoveryModal';
import LexoErrorCard from '@/components/LexoErrorCard';
import LexoLoadingCard from '@/components/LexoLoadingCard';
import PrivateFirmBanner from '@/components/PrivateFirmBanner';
import SideNav from '@/components/SideNav/SideNav';
import useIdentityStore from '@/stores/useIdentityStore';

export default function DashboardLayout() {
    const isAuthenticated = useIsAuthenticated();
    const { inProgress } = useMsal();
    const navigate = useNavigate();

    const callCount = useRef(0);
    const [identityFetchFailed, setIdentityFetchFailed] = useState(false);
    const userIdentity = useIdentityStore((state) => state.user);
    const fetchUserFromServer = useIdentityStore((state) => state.fetchUserFromServer);

    useEffect(() => {
        const fetchIdentityAndCheck = async () => {
            try {
                const identityResponse = await fetchUserFromServer();

                // Check if the user is in Lexo's system
                // and redirect to onboarding if they are not.
                if (!identityResponse?.data?.userId) {
                    // If the user isn't in Lexo's system, the API might return no data or no userId.
                    // Redirect to onboarding here.
                    navigate('/onboarding');
                }
            } catch (error) {
                console.log(error);
                if (isAxiosError(error)) {
                    if (error.code === 'ERR_AUTH_REQUIRED') {
                        // Auth flow is handling redirect. Do not route to onboarding for transient auth failures.
                        return;
                    }
                    // API throws 404 if the user is not present in Lexo's system.
                    if (error.response?.status === 404) {
                        navigate('/onboarding');
                        return;
                    }
                }

                setIdentityFetchFailed(true);
            }
        };

        // Immediately invoke the async function
        if (isAuthenticated && inProgress === InteractionStatus.None) {
            callCount.current += 1;
            if (callCount.current > 1) {
                return;
            }
            fetchIdentityAndCheck();
        }
        return () => {};
    }, [isAuthenticated, inProgress, fetchUserFromServer, navigate]);

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                boxSizing: 'border-box',
            }}
        >
            {userIdentity ? (
                <>
                    <SideNav />
                    <Box
                        sx={{
                            flexGrow: 1,
                            minWidth: 0,
                            height: '100vh',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <PrivateFirmBanner />
                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                            <Outlet />
                        </Box>
                    </Box>
                    <ApsDataExtractionDiscoveryModal />
                </>
            ) : identityFetchFailed ? (
                <LexoErrorCard />
            ) : (
                <LexoLoadingCard mainText={'Loading...'} />
            )}
        </Box>
    );
}
